// 🩺 치과 설정 — 첫 방문 등록에서 추가로 요청할 항목 (2026-09-30 대표 승인, 배포 전 플래그 OFF)
// 계약 docs/계약_첫방문등록_서버_2026-09-30.md §1-2: users/{clinicUid}.intakeFields = { history, meds, allergy, pregnancy, priorTreatment } (bool 5키 고정)
//  - 치과 **대표 본인만** update(rules intakeFieldsOk — 5키 hasOnly·bool). 직원은 읽기 전용(회사 문서를 읽어 표시만).
//  - 공통 항목(이름·생년월일·연락처·주소·내원 이유)은 항상 요청되며 끌 수 없다. 자유 질문 없음(의료법).
//  - 플래그(config/features.patientIntake) OFF면 이 화면은 안내만 보이고 스위치는 숨긴다. 서버가 제출 시점에 이 값을 스냅샷한다.
// 문구: docs/동의정본_첫방문등록_intake_2026-09-30.md §10-2 그대로
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ClipboardCheck } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { getFeatures, isPatientIntakeOn } from '../../utils/features';
import { INTAKE_COMMON_FIELDS, INTAKE_OPTIONAL_FIELDS, INTAKE_OPTIONAL_KEYS } from '../../utils/patientIntake';

const HELP = '켠 항목은 환자 동의 화면에 표시되고 동의 기록에 남습니다. 이름·생년월일·연락처·주소·내원 이유는 항상 요청됩니다. 자유 질문은 추가할 수 없습니다.';

// 저장값 → 5키 bool 맵 정규화 (필드 없음 = 전부 false)
const normalize = (v) => {
    const out = {};
    INTAKE_OPTIONAL_KEYS.forEach(k => { out[k] = !!(v && v[k] === true); });
    return out;
};

function ClinicIntakeFields({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const isStaff = user?.userType === 'staff';
    const clinicUid = isStaff ? user?.companyId : user?.uid;   // 직원은 회사(치과 대표) 문서를 읽기만
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    const [flagOn, setFlagOn] = useState(null);   // null=로딩
    const [me, setMe] = useState(undefined);      // undefined=로딩, null=문서 없음
    const [busyKey, setBusyKey] = useState('');
    const [notice, setNotice] = useState(null);   // { type:'ok'|'err', text }

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFlagOn(isPatientIntakeOn(f)); })
            .catch((e) => { console.error('[첫 방문 등록 설정] 플래그 조회 실패:', e?.message || e); if (alive) setFlagOn(false); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!clinicUid || !isClinic) return;
        const unsub = onSnapshot(doc(db, 'users', clinicUid),
            (snap) => setMe(snap.exists() ? snap.data() : null),
            (e) => { console.error('[첫 방문 등록 설정] 설정 조회 실패:', e?.code || e?.message || e); setMe(null); });
        return () => unsub();
    }, [clinicUid, isClinic]);

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title="첫 방문 등록 항목" />
                <div style={S.emptyBox}>치과 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }

    // 플래그 OFF: 섹션(스위치) 숨김 — 안내만
    if (flagOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title="첫 방문 등록 항목" />
                <div style={S.emptyBox}>첫 방문 등록 기능이 아직 열리지 않았습니다.</div>
            </div>
        );
    }

    const fields = normalize(me?.intakeFields);

    const toggle = async (key) => {
        if (isStaff || busyKey || me === undefined || !clinicUid) return;
        setBusyKey(key);
        setNotice(null);
        const next = { ...fields, [key]: !fields[key] };
        try {
            // 5키 전체 맵을 통째로 저장 — rules intakeFieldsOk(hasOnly 5키·bool) 충족
            await updateDoc(doc(db, 'users', clinicUid), { intakeFields: next });
            setNotice({ type: 'ok', text: '저장했습니다.' });
        } catch (e) {
            console.error('[첫 방문 등록 설정] 저장 실패:', e?.code || e?.message || e);
            setNotice({ type: 'err', text: e?.code === 'permission-denied' ? '치과 대표 계정만 변경할 수 있습니다.' : '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
        } finally {
            setBusyKey('');
        }
    };

    return (
        <div style={S.container}>
            <PageHeader icon={ClipboardCheck} title="첫 방문 등록 항목" />

            <div style={S.box}>
                <div style={S.sectionTitle}>항상 요청되는 항목</div>
                <div style={S.fixedRow}>
                    {INTAKE_COMMON_FIELDS.map(f => <span key={f.key} style={S.fixedChip}>{f.label}</span>)}
                </div>
                <div style={S.help}>공통 항목은 수정할 수 없습니다.</div>
            </div>

            <div style={S.box}>
                <div style={S.sectionTitle}>첫 방문 등록에서 추가로 요청할 항목</div>
                {isStaff && <div style={S.readonly}>직원 계정은 확인만 할 수 있습니다. 변경은 치과 대표 계정에서 합니다.</div>}
                {flagOn === null || me === undefined ? (
                    <div style={S.help}>불러오는 중…</div>
                ) : (
                    <div style={S.list}>
                        {INTAKE_OPTIONAL_FIELDS.map(f => {
                            const on = fields[f.key];
                            const disabled = isStaff || !!busyKey;
                            return (
                                <div key={f.key} style={S.item}>
                                    <span style={S.itemLabel}>{f.label}</span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={on}
                                        aria-label={f.label}
                                        disabled={disabled}
                                        onClick={() => toggle(f.key)}
                                        style={{ ...S.switch, background: on ? '#2563eb' : '#cbd5e1', cursor: disabled ? 'default' : 'pointer', opacity: busyKey === f.key ? 0.6 : 1 }}
                                    >
                                        <span style={{ ...S.knob, transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
                                    </button>
                                    <span style={{ ...S.state, color: on ? '#1d4ed8' : '#94a3b8' }}>{on ? '요청함' : '요청 안 함'}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div style={S.help}>{HELP}</div>
                {notice && <div style={notice.type === 'ok' ? S.ok : S.err}>{notice.text}</div>}
            </div>
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 720, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    sectionTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    fixedRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
    fixedChip: { fontSize: 12.5, fontWeight: 600, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '5px 11px' },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    list: { display: 'flex', flexDirection: 'column', gap: 4 },
    item: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px', borderBottom: '1px solid #f1f5f9' },
    itemLabel: { fontSize: 13.5, fontWeight: 600, color: '#0f172a', minWidth: 110 },
    switch: { position: 'relative', width: 44, height: 24, borderRadius: 999, border: 'none', padding: 0, flexShrink: 0, transition: 'background .15s' },
    knob: { position: 'absolute', top: 2, left: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.25)', transition: 'transform .15s' },
    state: { fontSize: 12, fontWeight: 600 },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
};

export default ClinicIntakeFields;
